import { $, throttledInterval, clearThrottled } from './utils.js';
import { applyLang, lang } from './i18n.js';
import { applyTheme, theme } from './theme.js';
import { initBlobBg } from './effects.js';
import './nav.js';
import './bean.js';
import './overlay.js';
import './contact.js';
import './terms.js';

/* ─── Init ────────────────────────────── */
applyTheme(theme);
applyLang(lang);
// Prices are on request now; pricing.js/renderPrices() is no longer used on
// the marketing site (pricing-config.json still feeds checkout.js).

// ─── Blob backgrounds ──────────────────
initBlobBg('hero-bg');
initBlobBg('menu-bg');

// Hero mascot sparkles (pause when hero is off-screen)
const heroSparkleContainer = document.querySelector('.mascot-wrap');
const heroSparkleSize = document.querySelector('.mascot');
const sparkleColors = ['var(--primary)', '#38bdf8', '#fbbf24', '#a78bfa', '#fff'];
let heroSparkleInterval;

function startHeroSparkles() {
	if (heroSparkleInterval) return;
	heroSparkleInterval = throttledInterval(() => {
		if (!heroSparkleContainer || !heroSparkleSize) return;
		if (heroSparkleContainer.offsetWidth === 0 && heroSparkleContainer.offsetHeight === 0) return;
		if (heroSparkleContainer.querySelectorAll('.sparkle').length > 30) return;
		const s = document.createElement('div');
		s.className = 'sparkle';
		const angle = Math.random() * Math.PI * 2;
		const dist = (heroSparkleSize.offsetWidth / 2) + Math.random() * 20;
		const cx = heroSparkleSize.offsetLeft + heroSparkleSize.offsetWidth / 2;
		const cy = heroSparkleSize.offsetTop + heroSparkleSize.offsetHeight / 2;
		s.style.left = (cx + Math.cos(angle) * dist) + 'px';
		s.style.top = (cy + Math.sin(angle) * dist) + 'px';
		s.style.background = sparkleColors[Math.floor(Math.random() * sparkleColors.length)];
		s.style.boxShadow = `0 0 6px ${s.style.background}`;
		s.style.width = (4 + Math.random() * 5) + 'px';
		s.style.height = s.style.width;
		s.style.setProperty('--dx', (Math.random() - 0.5) * 60 + 'px');
		s.style.setProperty('--dy', -(20 + Math.random() * 40) + 'px');
		heroSparkleContainer.appendChild(s);
		s.addEventListener('animationend', () => s.remove());
	}, 100);
}
function stopHeroSparkles() {
	clearThrottled(heroSparkleInterval);
	heroSparkleInterval = null;
}

if (heroSparkleContainer) {
	const heroObs = new IntersectionObserver(([entry]) => {
		if (entry.isIntersecting) startHeroSparkles();
		else stopHeroSparkles();
	}, { threshold: 0 });
	heroObs.observe(document.getElementById('hero'));
}

// ─── Pro card sparkles ──────────────
const proCard = $('#card-pro');
if (proCard) {
	let proSparkleInterval;
	function startProSparkles() {
		if (proSparkleInterval) return;
		proSparkleInterval = throttledInterval(() => {
			if (proCard.querySelectorAll('.sparkle').length > 15) return;
			const s = document.createElement('div');
			s.className = 'sparkle';
			const w = proCard.offsetWidth;
			const h = proCard.offsetHeight;
			// spawn along edges
			const edge = Math.random();
			let x, y;
			if (edge < 0.25) { x = Math.random() * w; y = -4; }
			else if (edge < 0.5) { x = Math.random() * w; y = h - 4; }
			else if (edge < 0.75) { x = -4; y = Math.random() * h; }
			else { x = w - 4; y = Math.random() * h; }
			s.style.left = x + 'px';
			s.style.top = y + 'px';
			s.style.background = sparkleColors[Math.floor(Math.random() * sparkleColors.length)];
			s.style.boxShadow = `0 0 6px ${s.style.background}`;
			s.style.width = (3 + Math.random() * 4) + 'px';
			s.style.height = s.style.width;
			s.style.setProperty('--dx', (Math.random() - 0.5) * 40 + 'px');
			s.style.setProperty('--dy', -(10 + Math.random() * 30) + 'px');
			proCard.appendChild(s);
			s.addEventListener('animationend', () => s.remove());
		}, 200);
	}
	function stopProSparkles() {
		clearThrottled(proSparkleInterval);
		proSparkleInterval = null;
	}
	const proObs = new IntersectionObserver(([entry]) => {
		if (entry.isIntersecting) startProSparkles();
		else stopProSparkles();
	}, { threshold: 0.1 });
	proObs.observe(proCard);
}

// ─── FAQ paw print background ────────
(function() {
	const canvas = $('#faq-paws');
	if (!canvas) return;
	const ctx = canvas.getContext('2d');
	let raf, visible = false;

	// SVG paw path from dog-paw.svg, viewBox 0 0 48.839 48.839
	const pawPath = new Path2D('M39.041,36.843c2.054,3.234,3.022,4.951,3.022,6.742c0,3.537-2.627,5.252-6.166,5.252c-1.56,0-2.567-0.002-5.112-1.326c0,0-1.649-1.509-5.508-1.354c-3.895-0.154-5.545,1.373-5.545,1.373c-2.545,1.323-3.516,1.309-5.074,1.309c-3.539,0-6.168-1.713-6.168-5.252c0-1.791,0.971-3.506,3.024-6.742c0,0,3.881-6.445,7.244-9.477c2.43-2.188,5.973-2.18,5.973-2.18h1.093v-0.001c0,0,3.698-0.009,5.976,2.181C35.059,30.51,39.041,36.844,39.041,36.843z M16.631,20.878c3.7,0,6.699-4.674,6.699-10.439S20.331,0,16.631,0S9.932,4.674,9.932,10.439S12.931,20.878,16.631,20.878z M10.211,30.988c2.727-1.259,3.349-5.723,1.388-9.971s-5.761-6.672-8.488-5.414s-3.348,5.723-1.388,9.971C3.684,29.822,7.484,32.245,10.211,30.988z M32.206,20.878c3.7,0,6.7-4.674,6.7-10.439S35.906,0,32.206,0s-6.699,4.674-6.699,10.439C25.507,16.204,28.506,20.878,32.206,20.878z M45.727,15.602c-2.728-1.259-6.527,1.165-8.488,5.414s-1.339,8.713,1.389,9.972c2.728,1.258,6.527-1.166,8.488-5.414S48.455,16.861,45.727,15.602z');

	function drawPaw(x, y, size, rot) {
		ctx.save();
		ctx.translate(x, y);
		ctx.rotate(rot);
		const s = size / 48.839;
		ctx.scale(s, s);
		ctx.translate(-24.42, -24.42);
		ctx.fill(pawPath);
		ctx.restore();
	}

	const paws = [];
	for (let i = 0; i < 12; i++) {
		paws.push({
			x: Math.random(), y: Math.random(),
			size: 90 + Math.random() * 180,
			rot: Math.random() * Math.PI * 2,
			vx: (Math.random() - 0.5) * 0.12,
			vy: (Math.random() - 0.5) * 0.12,
			vr: (Math.random() - 0.5) * 0.003,
		});
	}

	function animate() {
		if (!visible) return;
		canvas.width = canvas.offsetWidth;
		canvas.height = canvas.offsetHeight;
		const w = canvas.width, h = canvas.height;
		ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--on-surface').trim() || '#1c1917';
		for (const p of paws) {
			p.x += p.vx / w;
			p.y += p.vy / h;
			p.rot += p.vr;
			if (p.x < -0.15) p.x = 1.15;
			if (p.x > 1.15) p.x = -0.15;
			if (p.y < -0.15) p.y = 1.15;
			if (p.y > 1.15) p.y = -0.15;
			drawPaw(p.x * w, p.y * h, p.size, p.rot);
		}
		raf = requestAnimationFrame(animate);
	}

	const obs = new IntersectionObserver(([e]) => {
		visible = e.isIntersecting;
		if (visible && !raf) animate();
		if (!visible && raf) { cancelAnimationFrame(raf); raf = null; }
	}, { threshold: 0.01 });
	obs.observe(canvas.parentElement);
})();
